import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { DatePipe } from '@angular/common';
import { Paciente } from '../pacientes';
import { PacienteService } from '../../../core/services/pacientes.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { MunicipioServices } from '../../../core/services/municipio.service';
import { firstValueFrom } from 'rxjs';
// ==========================================
// MODELO PROVISIONAL — AJUSTAR CUANDO EXISTA
// EL ENDPOINT REAL DE RECETAS/ESTUDIOS
// ==========================================
export interface ResumenReceta {
  id: string;
  fecha: Date | string;
  diagnostico: string;
  indicaciones: string;
  medicamentos: { nombre: string; dosis: string; frecuencia: string }[];
  estudios: { nombre: string; resultado: string }[];
}

interface BarrioCatalogo {
  id: string;
  nombre: string;
  acronimo: string;
}

interface MunicipioCatalogo {
  id: string;
  nombre: string;
  acronimo: string;
  barrios: BarrioCatalogo[];
}

interface UbicacionPaciente {
  tipo: 'catalogo' | 'texto';
  valor: string;
}

@Component({
  selector: 'app-informacion-paciente',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatTabsModule, DatePipe],
  templateUrl: './informacion-pacientes.html',
  styleUrl: './informacion-pacientes.scss',
})
export class InformacionPaciente implements OnInit {
  recetas: ResumenReceta[] = [];
  cargandoRecetas = false;
  municipioNombre = 'Cargando...';
  barrioNombre = 'Cargando...';

  private municipios: MunicipioCatalogo[] = [];

  constructor(
    private dialogRef: MatDialogRef<InformacionPaciente>,
    @Inject(MAT_DIALOG_DATA) public paciente: Paciente,
    private pacienteService: PacienteService,
    private http: HttpClient,
    private municipioService: MunicipioServices,
  ) {}

  async ngOnInit(): Promise<void> {
    this.cargarRecetas();
    await this.cargarUbicaciones();
  }

  private async cargarUbicaciones(): Promise<void> {
    const municipio = (this.paciente as any).municipio as UbicacionPaciente | null | undefined;

    const barrio = (this.paciente as any).barrio as UbicacionPaciente | null | undefined;

    // Valores iniciales para evitar dejar "Cargando..." permanentemente.
    this.municipioNombre = 'No registrado';
    this.barrioNombre = 'No registrado';

    // Si se capturaron manualmente, mostrar el texto.
    if (municipio?.tipo === 'texto') {
      this.municipioNombre = municipio.valor?.trim() || 'No registrado';
    }

    if (barrio?.tipo === 'texto') {
      this.barrioNombre = barrio.valor?.trim() || 'No registrado';
    }

    // Si ninguno procede del catálogo, no hay nada que consultar.
    if (municipio?.tipo !== 'catalogo' && barrio?.tipo !== 'catalogo') {
      return;
    }

    try {
      const respuesta: any = await firstValueFrom(this.municipioService.obtenerMunicipio());

      this.municipios = Array.isArray(respuesta) ? respuesta : (respuesta?.data ?? []);

      // Buscar el municipio por su ID.
      const municipioEncontrado = this.municipios.find(
        (item) => String(item.id) === String(municipio?.valor),
      );

      if (municipio?.tipo === 'catalogo') {
        this.municipioNombre =
          municipioEncontrado?.nombre ?? 'Municipio no encontrado en el catálogo';
      }

      // Buscar el barrio dentro del municipio correspondiente.
      let barrioEncontrado: BarrioCatalogo | undefined;

      if (barrio?.tipo === 'catalogo') {
        barrioEncontrado = municipioEncontrado?.barrios?.find(
          (item) => String(item.id) === String(barrio.valor),
        );

        // Si el municipio se escribió manualmente, buscar el barrio
        // en todos los municipios del catálogo por su ID.
        if (!barrioEncontrado) {
          for (const item of this.municipios) {
            barrioEncontrado = item.barrios?.find((b) => String(b.id) === String(barrio.valor));

            if (barrioEncontrado) {
              break;
            }
          }
        }

        this.barrioNombre = barrioEncontrado?.nombre ?? 'Barrio no encontrado en el catálogo';
      }
    } catch (error) {
      console.error('Error al cargar el catálogo de ubicaciones:', error);

      if (municipio?.tipo === 'catalogo') {
        this.municipioNombre = 'No se pudo cargar el municipio';
      }

      if (barrio?.tipo === 'catalogo') {
        this.barrioNombre = 'No se pudo cargar el barrio';
      }
    }
  }

  cerrar() {
    this.dialogRef.close();
  }

  cargarRecetas(): void {
    if (!this.paciente.id) {
      return;
    }

    this.cargandoRecetas = true;
    this.cargandoRecetas = false;
  }

  obtenerEdad(): string {
    if (!this.paciente.fechaNacimiento) {
      return '';
    }

    const nacimiento = new Date(this.paciente.fechaNacimiento);
    const hoy = new Date();

    let anios = hoy.getFullYear() - nacimiento.getFullYear();
    let meses = hoy.getMonth() - nacimiento.getMonth();
    let dias = hoy.getDate() - nacimiento.getDate();

    if (dias < 0) {
      meses--;
      const mesAnterior = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
      dias += mesAnterior.getDate();
    }

    if (meses < 0) {
      anios--;
      meses += 12;
    }

    if (anios < 0) {
      return '';
    }

    if (anios === 0 && meses === 0) {
      return dias === 1 ? '1 día' : `${dias} días`;
    }

    if (anios === 0) {
      return meses === 1 ? '1 mes' : `${meses} meses`;
    }

    if (anios <= 10) {
      const textoAnios = anios === 1 ? '1 año' : `${anios} años`;
      if (meses === 0) return textoAnios;
      const textoMeses = meses === 1 ? '1 mes' : `${meses} meses`;
      return `${textoAnios} y ${textoMeses}`;
    }

    return anios === 1 ? '1 año' : `${anios} años`;
  }
}
